import { z } from 'zod'
import { BRIEFING_PROVIDERS } from './ai-models'

/**
 * Objekt-Briefing — a short evaluation of a WEG's Eigentümerversammlung
 * protocols and Beschlusssammlung, written by a language model from PDFs read
 * out of onOffice and rendered to the agency's PDF template.
 *
 * THE SECTION STRUCTURE IS CODE, the content instruction is a setting (see
 * `briefing-prompt.ts` and `settings.ts`). The renderer and this schema depend
 * on the structure; what the agency words is what the agency edits.
 *
 * NOTHING ABOUT MONEY IS IN HERE except the billed estimate. Purchase price,
 * markup, provider cost and TOKEN COUNTS are absent by contract: tokens times
 * the public list price of a model whose name is visible is the purchase price.
 * The visible measure of a briefing's size is documents, pages and bytes.
 */

/**
 * A nullable TEXT leaf of the body: a string up to `max`, or null — and an
 * empty or blank string IS null.
 *
 * WHY. Anthropic's structured outputs accept at most 16 parameters with a
 * union type per request, and every `string | null` is one (`anyOf`). Prompt
 * version 2 has twenty. So the OUTPUT schema the model is held to has no
 * nullables at all (`briefings/output-schema.ts` collapses each to a plain
 * string, "leer, wenn es nichts gibt"), and this preprocess turns the empty
 * answer back into the null the renderers and the "—" rule work with. Stored
 * bodies go through the same parse, so "" and null can never mean two things.
 */
function nullableText(max: number) {
  return z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().max(max).nullable(),
  )
}

/*
 * The body — what the model returns and the PDF prints.
 *
 * Every object is STRICT and every string and list is capped. THE CAPS DO NOT
 * HOLD THE LENGTH: structured output does not enforce maxLength or maxItems,
 * so the model only learns about them by failing. The length is held by the
 * budget in the prompt's frame (`briefing-prompt.ts`); the caps are the safety
 * net behind it — an answer that ignores the budget is a failed (and paid) run,
 * not a document that silently drops part of itself or overflows the three
 * pages it is meant to fill.
 *
 * WHY MONEY IS FREE TEXT. "500.000 € pauschal", "3,5 % der Auftragssumme",
 * "rund 100.000 €" are what the protocols say. A number field would force the
 * model to invent a precision the source does not have — and a briefing that
 * states a figure more exactly than the protocol did is wrong in the one place
 * a buyer reads twice.
 */
export const briefingBodySchema = z.strictObject({
  introduction: z.strictObject({
    /** Two sentences: the biggest decided measure and how it is financed. */
    summary: z.string().max(600),
    basis: z.strictObject({
      /** "Protokoll ETV 23.07.2025", "Beschlusssammlung 02/2023–07/2025" */
      documents: z.array(z.string().max(120)).max(12),
      administrator: nullableText(160),
      /** "Ein Protokoll der ETV 2026 liegt nicht vor." */
      missingNote: nullableText(300),
    }),
  }),
  /**
   * One entry per Sonderfrage the agent asked, in that order; EMPTY when none
   * was asked. `mentioned: false` prints as `BRIEFING_NOT_MENTIONED` — printed
   * by the renderer, never by the model — then `finding`, then the indirect
   * hints: the case where the documents never name the topic but say
   * something adjacent to it.
   */
  specialQuestions: z
    .array(
      z.strictObject({
        question: z.string().max(200),
        mentioned: z.boolean(),
        /** What follows "Nicht erwähnt." — the renderer prints that itself. */
        finding: z.string().max(600),
        indirectHints: z.array(z.string().max(300)).max(4),
      }),
    )
    .max(6),
  renovations: z.strictObject({
    /** Heading suffix, e.g. "(ETV 23.07.2025)". */
    context: nullableText(80),
    measures: z
      .array(
        z.strictObject({
          measure: z.string().max(160),
          company: nullableText(120),
          /** Free text on purpose: "500.000 € pauschal", "3,5 % der Auftragssumme". */
          grossCost: nullableText(80),
        }),
      )
      .max(12),
    /** Sonderumlage or Darlehen: amount, term, rate, deadlines, buyer, liability. */
    financing: z.array(z.string().max(400)).max(8),
  }),
  /**
   * Zustand und Sanierungshistorie (since prompt version 2): what was DONE
   * and when, plus known defects and Gutachten — what is five years old and
   * what is forty-five. The decided-but-not-done measures stay in
   * `renovations`; the frame says which goes where.
   */
  condition: z.strictObject({
    items: z
      .array(
        z.strictObject({
          measure: z.string().max(160),
          /** Free text: "2019", "2016/17", "um 2010". */
          year: nullableText(40),
          note: nullableText(300),
        }),
      )
      .max(12),
  }),
  costRisks: z
    .array(
      z.strictObject({
        topic: z.string().max(120),
        status: z.string().max(400),
      }),
    )
    .max(12),
  finances: z.strictObject({
    specialLevies: nullableText(400),
    reserve: nullableText(300),
    settlementPeaks: nullableText(300),
    businessPlan: nullableText(300),
    /*
     * The key figures (since prompt version 2), as the documents state them —
     * free text for the same reason as the money above: "3.120 € p. a." and
     * "260 € monatlich" are both what a Wirtschaftsplan says.
     */
    /** Hausgeld gesamt, per year or per month — the documents say which. */
    hausgeld: nullableText(200),
    /** Jährliche Zuführung zur Erhaltungsrücklage. */
    reserveContribution: nullableText(200),
    /** Rücklage je m² Wohnfläche, je MEA or je Einheit — whatever the documents allow. */
    reserveRatio: nullableText(200),
    /** Anzahl der Einheiten. */
    units: nullableText(80),
    /**
     * The model's OWN judgement of the reserve's adequacy — the one place
     * outside the two checklists where one is asked for, and labelled as such
     * by the renderer ("Einschätzung (eigene)"), never by the model.
     */
    assessment: nullableText(300),
  }),
  /**
   * Entwicklung (since prompt version 2): one row per year the documents give
   * figures for, never interpolated. Three Sonderumlagen in five years say
   * more than any sentence.
   */
  development: z
    .array(
      z.strictObject({
        year: z.string().max(40),
        hausgeld: nullableText(80),
        reserve: nullableText(80),
        specialLevy: nullableText(80),
      }),
    )
    .max(8),
  /** Gemeinschaft und Rechtliches (since prompt version 2). */
  community: z.array(z.string().max(400)).max(8),
  administrationAndSale: z.array(z.string().max(400)).max(8),
  /**
   * Hinweise zur Einheit (since prompt version 2). `designation` echoes the
   * unit the user prompt named (`briefingUnitDesignation`), null when it named
   * none — and then `items` is empty: without a name the model has nothing to
   * filter the documents for.
   */
  unit: z.strictObject({
    designation: nullableText(80),
    items: z.array(z.string().max(300)).max(6),
  }),
  /**
   * Grundbuch (since prompt version 3): what the Grundbuchauszug of the
   * unit shows in Abteilung II (Lasten und Beschränkungen — Wegerechte,
   * Nießbrauch, Wohnrechte, Vorkaufsrechte, Zwangsversteigerungs- und
   * Insolvenzvermerke) and Abteilung III (Grundpfandrechte — Grundschulden
   * and Hypotheken, each WITH or WITHOUT a Brief). The buyer's question is
   * whether anything registered survives the sale, and a Briefgrundschuld
   * needs the Brief handed over before it can be deleted.
   *
   * `source` is the Grundbuchauszug the entries were read from, or null when
   * none was among the documents — and then both lists are empty. "No
   * Auszug" and "an Auszug with nothing registered" must stay two different
   * statements; the renderer prints them differently (`briefingSectionState`).
   */
  landRegister: z.strictObject({
    /** "Grundbuchauszug vom TT.MM.JJJJ, Blatt n" — or null when none was read. */
    source: nullableText(160),
    /** Abteilung II. */
    encumbrances: z
      .array(
        z.strictObject({
          /** "Wegerecht", "Nießbrauch", "Zwangsversteigerungsvermerk" … */
          kind: z.string().max(120),
          /** Beneficiary, lfd. Nr., wording — or null. */
          detail: nullableText(300),
        }),
      )
      .max(10),
    /** Abteilung III. */
    landCharges: z
      .array(
        z.strictObject({
          /** "Grundschuld", "Hypothek" … */
          kind: z.string().max(80),
          /** Free text, as the Auszug states it: "150.000 €", "250.000 DM". */
          amount: nullableText(80),
          /**
           * Whether the Grundpfandrecht was registered with a Brief. An ENUM,
           * not a nullable text: this is the one classification the agency
           * asked for by name, and three fixed values are what the renderer
           * can label. Not a union in JSON Schema (Anthropic's 16-union cap).
           */
          certificate: z.enum(['mit_brief', 'ohne_brief', 'unbekannt']),
          creditor: nullableText(160),
          /** "Löschung bewilligt", "lfd. Nr. 3", "abgetreten an …" — or null. */
          note: nullableText(300),
        }),
      )
      .max(10),
  }),
  /** Printed under "Eigene Empfehlung, nicht aus den Protokollen." */
  toClarify: z.array(z.string().max(300)).max(12),
  /**
   * What the BUYER should ask the SELLER (since prompt version 2) — the
   * second checklist, also the tool's own advice and printed under the same
   * note.
   */
  toClarifySeller: z.array(z.string().max(300)).max(8),
})

export type BriefingBody = z.infer<typeof briefingBodySchema>

/**
 * A stored body brought up to the CURRENT schema before it is parsed.
 *
 * A body written under an earlier prompt version lacks the keys added since,
 * and the strict schema would refuse it — every existing briefing would read
 * "passt nicht mehr" overnight. So the missing keys are filled with their
 * empty value (`[]`, null), and only where ABSENT: a key that is there is
 * never touched, so a broken body stays broken and still fails the parse.
 * The renderers then omit the later sections by `promptVersion`
 * (`briefingSectionState`), which is why "empty" here never prints as
 * "Keine Angaben".
 *
 * Each version's keys are filled only for a body OLDER than that version:
 * a version-2 body gets `landRegister` and nothing else. Returns the input
 * unchanged for the current version and for anything that is not an object.
 */
export function upgradeBriefingBody(raw: unknown, promptVersion: number): unknown {
  if (promptVersion >= 3 || typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return raw
  }
  const body: Record<string, unknown> = { ...(raw as Record<string, unknown>) }
  const fill = (key: string, empty: unknown) => {
    if (!(key in body)) body[key] = empty
  }
  if (promptVersion < 2) {
    fill('condition', { items: [] })
    fill('development', [])
    fill('community', [])
    fill('unit', { designation: null, items: [] })
    fill('toClarifySeller', [])
    const finances = body.finances
    if (typeof finances === 'object' && finances !== null && !Array.isArray(finances)) {
      const upgraded: Record<string, unknown> = { ...(finances as Record<string, unknown>) }
      for (const key of [
        'hausgeld',
        'reserveContribution',
        'reserveRatio',
        'units',
        'assessment',
      ]) {
        if (!(key in upgraded)) upgraded[key] = null
      }
      body.finances = upgraded
    }
  }
  // Version 3: the Grundbuch section.
  fill('landRegister', { source: null, encumbrances: [], landCharges: [] })
  return body
}

/* ----------------------------------------------------------- labels --- */

/** Under the object address in the title block, as in the agency's template. */
export const BRIEFING_SUBTITLE = 'Auswertung der Protokolle und der Beschlusssammlung'

/**
 * Printed above `toClarify` and `toClarifySeller`. The two sections that are
 * the tool's own advice rather than what the protocols say, and they have to
 * say so on the page.
 */
export const BRIEFING_RECOMMENDATION_NOTE = 'Eigene Empfehlung, nicht aus den Protokollen.'

/**
 * Section headings, shared by the PDF and the detail page so the two cannot
 * name a section differently.
 *
 * `introduction` has none: in the agency's example the opening sentences and
 * the "Grundlage:" line sit directly under the title. Each Sonderfrage is
 * headed by its own question. `renovations` is composed with its `context`
 * ("Sanierung und Darlehen (ETV 23.07.2025)") — see `renovationsHeading`.
 */
export const BRIEFING_SECTION_TITLES = {
  introduction: null,
  renovations: 'Sanierung und Darlehen',
  condition: 'Zustand und Sanierungshistorie',
  costRisks: 'Weitere Kostenrisiken',
  finances: 'Finanzen',
  development: 'Entwicklung',
  community: 'Gemeinschaft und Rechtliches',
  administrationAndSale: 'Verwaltung und Verkauf',
  unit: 'Hinweise zur Einheit',
  landRegister: 'Grundbuch',
  toClarify: 'Mit der Verwaltung zu klären',
  toClarifySeller: 'Mit dem Verkäufer zu klären',
} as const

/**
 * Row labels of the Finanzen block, in print order. The RENDERER prints them in
 * front of each value, so the prompt tells the model to give the value without
 * its label — otherwise the PDF says "Wirtschaftsplan: Wirtschaftsplan 2026: …".
 */
export const BRIEFING_FINANCE_LABELS: Record<keyof BriefingBody['finances'], string> = {
  specialLevies: 'Sonderumlagen',
  reserve: 'Erhaltungsrücklage',
  settlementPeaks: 'Abrechnungsspitzen',
  businessPlan: 'Wirtschaftsplan',
  hausgeld: 'Hausgeld',
  reserveContribution: 'Zuführung Rücklage',
  reserveRatio: 'Rücklage je m²/Einheit',
  units: 'Einheiten',
  assessment: 'Einschätzung (eigene)',
}

/** Column heads of the tables. */
export const BRIEFING_TABLE_HEADINGS = {
  measures: ['Maßnahme', 'Firma', 'Kosten brutto'],
  condition: ['Maßnahme', 'Jahr', 'Hinweis'],
  costRisks: ['Thema', 'Stand'],
  development: ['Jahr', 'Hausgeld', 'Rücklage', 'Sonderumlage'],
  encumbrances: ['Recht', 'Hinweis'],
  landCharges: ['Grundpfandrecht', 'Betrag', 'Brief', 'Gläubiger', 'Hinweis'],
} as const

/*
 * The Grundbuch section's wording, shared by the PDF and the detail page.
 *
 * The two Abteilungen are printed as their own labelled parts, each either a
 * table or "Keine Eintragungen." — a statement, because "nothing registered
 * in Abteilung III" is exactly what a buyer wants to read, and it must not
 * look like a part the tool forgot to print.
 */
export const BRIEFING_LAND_REGISTER_LABELS = {
  source: 'Grundbuchauszug',
  encumbrances: 'Abteilung II – Lasten und Beschränkungen',
  landCharges: 'Abteilung III – Grundpfandrechte',
} as const

/** Printed under an Abteilung's label when the Auszug shows nothing there. */
export const BRIEFING_NO_ENTRIES = 'Keine Eintragungen.'

/** Printed under the Grundbuch heading when no Grundbuchauszug was among the documents. */
export const BRIEFING_NO_LAND_REGISTER = 'Kein Grundbuchauszug in den Unterlagen.'

/**
 * The "Brief" column of a Grundpfandrecht. `unbekannt` prints as "nicht
 * angegeben": the Auszug names a Brief or its absence on every entry, and an
 * entry where the model could not tell is a line for the agent to check.
 */
export const BRIEFING_LAND_CHARGE_CERTIFICATE_LABELS: Record<
  BriefingBody['landRegister']['landCharges'][number]['certificate'],
  string
> = {
  mit_brief: 'mit Brief',
  ohne_brief: 'ohne Brief',
  unbekannt: 'nicht angegeben',
}

/**
 * The line that opens the Grundbuch section: "Grundbuchauszug: vom
 * TT.MM.JJJJ, Blatt n". The frame asks for the value without the label, but
 * a model answer that opens with the word anyway ("Grundbuchauszug vom …",
 * "Grundbuchauszug: …") loses it here, so the line never reads
 * "Grundbuchauszug: Grundbuchauszug vom …".
 */
export function briefingLandRegisterSource(source: string): { label: string; text: string } {
  const label = BRIEFING_LAND_REGISTER_LABELS.source
  const text = source
    .trim()
    .replace(new RegExp(`^${label}\\s*:?\\s*`, 'i'), '')
    .trim()
  return { label: `${label}:`, text: briefingValue(text) }
}

/*
 * WHAT A BRIEFING OF AN OLDER PROMPT VERSION PRINTS.
 *
 * A section introduced in version 2 is OMITTED for a briefing made under
 * version 1 — the question was never put to the model, and "Keine Angaben in
 * den Unterlagen." under it would be a statement nobody made. Same reasoning
 * as the Auszug confirmations' null in the handover protocols. The finance
 * key figures follow the same rule, line by line.
 */
export const BRIEFING_SECTION_SINCE = {
  condition: 2,
  development: 2,
  community: 2,
  unit: 2,
  toClarifySeller: 2,
  landRegister: 3,
} as const satisfies Partial<Record<keyof BriefingBody, number>>

export type BriefingVersionedSection = keyof typeof BRIEFING_SECTION_SINCE

export const BRIEFING_FINANCE_SINCE: Partial<Record<keyof BriefingBody['finances'], number>> = {
  hausgeld: 2,
  reserveContribution: 2,
  reserveRatio: 2,
  units: 2,
  assessment: 2,
}

/**
 * Printed under the heading of an empty `condition` or `community`: a
 * statement, like the "—" in Finanzen. The section was asked about, and a
 * reader has to see that the answer was "nothing".
 */
export const BRIEFING_NO_FINDINGS = 'Keine Angaben in den Unterlagen.'

/**
 * Printed under the advice note of an EMPTY checklist — `toClarify` and
 * `toClarifySeller` alike. Not `BRIEFING_NO_FINDINGS`: the list is the tool's
 * own advice, and a sentence about what the documents contain, right under
 * "nicht aus den Protokollen", would contradict the note above it.
 */
export const BRIEFING_EMPTY_CHECKLIST = '—'

/** What an `empty` section prints (the unit has its own sentence and line). */
export function briefingEmptyText(
  key: 'condition' | 'community' | 'toClarifySeller' | 'landRegister',
): string {
  switch (key) {
    case 'toClarifySeller':
      return BRIEFING_EMPTY_CHECKLIST
    case 'landRegister':
      return BRIEFING_NO_LAND_REGISTER
    default:
      return BRIEFING_NO_FINDINGS
  }
}

/** Printed for a named unit the documents say nothing about. */
export const BRIEFING_UNIT_NO_FINDINGS = 'Keine Angaben zu dieser Einheit in den Unterlagen.'

/**
 * Whether a version-2 section is printed, and how — THE rule, once, for the
 * PDF and the detail page alike:
 *
 * - `omit`: no heading at all — a version-1 briefing, an empty `development`
 *   (an empty trend table says nothing the Finanzen block does not), a `unit`
 *   without a designation (no unit was named, so none was asked about);
 * - `empty`: the heading and `briefingEmptyText(key)` — "Keine Angaben in
 *   den Unterlagen." for the findings, "—" under the advice note for the
 *   seller checklist (`BRIEFING_UNIT_NO_FINDINGS` under the unit's line for
 *   `unit`, `BRIEFING_NO_LAND_REGISTER` for a `landRegister` without a
 *   source — no Auszug was read, which is not "nothing is registered");
 * - `content`: the heading and what the model found. For `landRegister` that
 *   is the Auszug line and both Abteilungen, each a table or "Keine
 *   Eintragungen." — an Auszug with empty lists IS content.
 */
export function briefingSectionState(
  body: BriefingBody,
  key: BriefingVersionedSection,
  promptVersion: number,
): 'content' | 'empty' | 'omit' {
  if (promptVersion < BRIEFING_SECTION_SINCE[key]) return 'omit'
  switch (key) {
    case 'condition':
      return body.condition.items.length > 0 ? 'content' : 'empty'
    case 'development':
      return body.development.length > 0 ? 'content' : 'omit'
    case 'community':
      return body.community.length > 0 ? 'content' : 'empty'
    case 'unit':
      if (!body.unit.designation?.trim()) return 'omit'
      return body.unit.items.length > 0 ? 'content' : 'empty'
    case 'toClarifySeller':
      return body.toClarifySeller.length > 0 ? 'content' : 'empty'
    case 'landRegister':
      return body.landRegister.source?.trim() ? 'content' : 'empty'
  }
}

/** The line that opens "Hinweise zur Einheit": which unit the notes are about. */
export function briefingUnitLine(designation: string): string {
  return `Einheit: ${designation.trim()}`
}

/** Printed for a Sonderfrage the documents do not mention. */
export const BRIEFING_NOT_MENTIONED = 'Nicht erwähnt.'

export function renovationsHeading(context: string | null): string {
  const suffix = context?.trim()
  return suffix
    ? `${BRIEFING_SECTION_TITLES.renovations} ${suffix}`
    : BRIEFING_SECTION_TITLES.renovations
}

/* -------------------------------------------------------- wording --- */

/*
 * What the PDF and the detail page SAY around the model's content. Here, once,
 * because the renderer and the web view must word a briefing identically — two
 * copies is how a document and its preview come to disagree.
 */

/** Empty prints as "—": a blank and a missing value read differently later. */
export function briefingValue(text: string | null | undefined): string {
  const trimmed = (text ?? '').trim()
  return trimmed === '' ? '—' : trimmed
}

/** "." unless the text already ends in punctuation. */
export function briefingSentence(text: string): string {
  const trimmed = text.trim()
  return /[.!?…:]$/.test(trimmed) ? trimmed : `${trimmed}.`
}

/** "a, b, c sowie d" */
export function briefingEnumerate(items: string[]): string {
  const clean = items.map((item) => item.trim()).filter(Boolean)
  if (clean.length <= 1) return clean.join('')
  return `${clean.slice(0, -1).join(', ')} sowie ${clean.at(-1)}`
}

/*
 * "Verwalter ist die SKYLINE GmbH, Schwetzingen." is the example's wording,
 * and the article only fits a company. A person or a name without a legal
 * form gets the neutral "Verwalter: …" rather than "ist die Herr Müller".
 */
const FEMININE_ADMINISTRATOR = /\b(GmbH|mbH|AG|UG|KG|GbR|OHG|Hausverwaltung|Verwaltung)\b/i

/** The text after "Grundlage:" — documents, administrator, what is missing. */
export function briefingBasisText(basis: BriefingBody['introduction']['basis']): string | null {
  const parts: string[] = []
  const documents = briefingEnumerate(basis.documents)
  if (documents) parts.push(briefingSentence(documents))
  // "die SKYLINE GmbH" from the model would otherwise read "ist die die …".
  const administrator = basis.administrator?.trim().replace(/^(die|der)\s+/i, '')
  if (administrator) {
    parts.push(
      FEMININE_ADMINISTRATOR.test(administrator)
        ? briefingSentence(`Verwalter ist die ${administrator}`)
        : briefingSentence(`Verwalter: ${administrator}`),
    )
  }
  const missing = basis.missingNote?.trim()
  if (missing) parts.push(briefingSentence(missing))
  return parts.length > 0 ? parts.join(' ') : null
}

/**
 * Splits a value that already opens with its own label.
 *
 * The example's Finanzen line reads "Wirtschaftsplan 2026: Ob die
 * Darlehensrate …" — the label carries the year. Printing our label in front
 * of that would read "Wirtschaftsplan: Wirtschaftsplan 2026: …"; so when the
 * value opens with the label and at most a short qualifier (a year, a date)
 * before a colon, that prefix becomes the label instead. Only that shape:
 * "Sonderumlagen sind keine bekannt (Stand: 05/2024)" must not be split at
 * the colon inside the parenthesis.
 */
export function briefingLabelled(label: string, text: string): { label: string; text: string } {
  const trimmed = text.trim()
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const own = new RegExp(`^${escaped}(\\s+[\\d./–-]+)?:`, 'i').exec(trimmed)
  if (own) {
    return { label: own[0], text: trimmed.slice(own[0].length).trim() }
  }
  return { label: `${label}:`, text: trimmed }
}

/**
 * The Finanzen lines in print order, each split by `briefingLabelled`.
 *
 * `promptVersion` decides which lines exist: a version-1 briefing prints the
 * four it was asked for, never five "—" for key figures nobody requested.
 */
export function briefingFinanceLines(
  finances: BriefingBody['finances'],
  promptVersion: number,
): { label: string; text: string }[] | null {
  const keys = (Object.keys(BRIEFING_FINANCE_LABELS) as (keyof BriefingBody['finances'])[]).filter(
    (key) => promptVersion >= (BRIEFING_FINANCE_SINCE[key] ?? 1),
  )
  if (!keys.some((key) => (finances[key] ?? '').trim() !== '')) return null
  return keys.map((key) =>
    briefingLabelled(BRIEFING_FINANCE_LABELS[key], briefingValue(finances[key])),
  )
}

/** A finding that opens with "Nicht erwähnt." would say it twice after the bold label. */
export function briefingNotMentionedFinding(finding: string): string {
  const trimmed = finding.trim()
  return trimmed.startsWith(BRIEFING_NOT_MENTIONED)
    ? trimmed.slice(BRIEFING_NOT_MENTIONED.length).trim()
    : trimmed
}

/* ------------------------------------------------------------- rows --- */

export const briefingStatusSchema = z.enum(['queued', 'running', 'succeeded', 'failed'])
export type BriefingStatus = z.infer<typeof briefingStatusSchema>

export const BRIEFING_STATUS_LABELS: Record<BriefingStatus, string> = {
  queued: 'wartet',
  running: 'läuft',
  succeeded: 'fertig',
  failed: 'fehlgeschlagen',
}

export const briefingTriggerSchema = z.enum(['manual', 'scheduled'])
export type BriefingTrigger = z.infer<typeof briefingTriggerSchema>

/**
 * A document the briefing was made from, as it was when it was read. Stored
 * with the row, because the "Grundlage" of a briefing is part of its meaning
 * and onOffice's listing moves on.
 */
export const briefingSourceFileSchema = z.object({
  id: z.number().int(),
  title: z.string().nullable(),
  originalName: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  modifiedAt: z.iso.datetime().nullable(),
  /** Null when the page count could not be read from the file. */
  pages: z.number().int().nonnegative().nullable(),
  /**
   * Pages that had no usable text layer and were read by OCR (the Mistral
   * driver). Null when the run's provider read the PDF itself, and for rows
   * from before the field existed — a default, so stored rows keep parsing.
   */
  ocrPages: z.number().int().nonnegative().nullable().default(null),
  /**
   * The lowest page-level OCR confidence (0–1) among those pages, as the OCR
   * model reported it. Shown as a warning below `BRIEFING_OCR_CONFIDENCE_WARNING`;
   * it never fails a run — the agent reading the briefing is the review.
   */
  ocrMinConfidence: z.number().min(0).max(1).nullable().default(null),
})
export type BriefingSourceFile = z.infer<typeof briefingSourceFileSchema>

/** Below this page confidence the UI warns that a scanned document may be misread. */
export const BRIEFING_OCR_CONFIDENCE_WARNING = 0.8

/**
 * The object, frozen at creation like a protocol's estate snapshot, so the
 * briefing still renders after the estate is archived or leaves the mirror.
 */
export const briefingEstateSchema = z.object({
  estateId: z.number().int().nullable(),
  externalNumber: z.string().nullable(),
  title: z.string().nullable(),
  street: z.string().nullable(),
  houseNumber: z.string().nullable(),
  postalCode: z.string().nullable(),
  city: z.string().nullable(),
})
export type BriefingEstate = z.infer<typeof briefingEstateSchema>

export const briefingSchema = z.object({
  id: z.string(),
  estate: briefingEstateSchema,
  sourceFiles: z.array(briefingSourceFileSchema),
  specialQuestions: z.string().nullable(),
  status: briefingStatusSchema,
  /** Null until the run succeeds. */
  body: briefingBodySchema.nullable(),
  error: z.string().nullable(),
  /** Who ran the call and the model id. Visible by design: an admin chose both (D3). */
  provider: z.enum(BRIEFING_PROVIDERS),
  model: z.string(),
  promptVersion: z.number().int().positive(),
  trigger: briefingTriggerSchema,
  createdBy: z.object({ name: z.string() }).nullable(),
  createdAt: z.iso.datetime(),
  startedAt: z.iso.datetime().nullable(),
  finishedAt: z.iso.datetime().nullable(),
  /**
   * The link written into the estate in onOffice, so a colleague there lands
   * on this briefing.
   *
   * No field name and no URL: the field is server configuration, and the web
   * app knows its own address. `available` says whether the server has the
   * write enabled at all — independent of this briefing's status.
   * `current` is false when another briefing of the same object was linked
   * LATER: the field in onOffice holds one URL, and the newest write wins.
   */
  onOfficeLink: z.object({
    available: z.boolean(),
    linkedAt: z.iso.datetime().nullable(),
    linkedBy: z.object({ name: z.string() }).nullable(),
    current: z.boolean(),
  }),
})
export type Briefing = z.infer<typeof briefingSchema>

/**
 * The object as the title names it: "Hardtwaldring 99, 68723 Oftersheim".
 *
 * The house number is CUT AT ITS FIRST COMMA. onOffice keeps the unit in that
 * field ("99, WE49, 8.OG"), and a briefing evaluates the WEG's protocols — it
 * is about the building, not the flat being sold. The example document is
 * titled with the building's address alone.
 */
export function briefingAddress(estate: BriefingEstate): string | null {
  // A number without a street ("99, 68723 Oftersheim") names nothing; the
  // caller then falls back to the estate title.
  const streetName = estate.street?.trim() ?? ''
  if (!streetName) return null
  const number = estate.houseNumber?.split(',')[0]?.trim() ?? ''
  const street = [streetName, number].filter(Boolean).join(' ')
  const place = [estate.postalCode?.trim() ?? '', estate.city?.trim() ?? '']
    .filter(Boolean)
    .join(' ')
  return place ? `${street}, ${place}` : street
}

/**
 * The unit within the building: the house number after its FIRST comma —
 * "99, WE49, 8.OG" → "WE49, 8.OG". Null for a plain house number or an empty
 * remainder. The cut is the one `briefingAddress` makes, seen from the other
 * side: the title takes what is before the comma, the prompt's "Einheit:"
 * line what is after it.
 */
export function briefingUnitDesignation(estate: BriefingEstate): string | null {
  const number = estate.houseNumber ?? ''
  const comma = number.indexOf(',')
  if (comma === -1) return null
  const unit = number.slice(comma + 1).trim()
  return unit === '' ? null : unit
}

/** The document's name: the address, else the title, else the object number. */
export function briefingDocumentName(briefing: Pick<Briefing, 'id' | 'estate'>): string {
  const { estate } = briefing
  return (
    briefingAddress(estate) ??
    (estate.title?.trim() ||
      (estate.externalNumber
        ? `Objekt ${estate.externalNumber}`
        : `Briefing ${briefing.id.slice(0, 8)}`))
  )
}

export const briefingListSchema = z.object({
  briefings: z.array(briefingSchema),
  /** False without an API key — the page says so instead of failing a run. */
  configured: z.boolean(),
  /** The month's limit is spent; creating is refused until the next month. */
  limitReached: z.boolean(),
  /**
   * The admin's default Sonderfragen (Einstellungen), so the dialog can prefill
   * them for everybody — the settings route itself is admin-only. Empty when
   * none is set.
   */
  defaultSpecialQuestions: z.string(),
})
export type BriefingList = z.infer<typeof briefingListSchema>

/* ----------------------------------------------------------- inputs --- */

/**
 * What the client sends. File IDS only: the server reads the files from
 * onOffice itself, so a caller cannot hand it a document from another object.
 * No prompt field, for the same reason as Bild-KI — the server builds it.
 */
export const createBriefingSchema = z.object({
  estateId: z.number().int().positive(),
  fileIds: z.array(z.number().int().positive()).min(1).max(40),
  specialQuestions: z.string().trim().max(2000).nullable().default(null),
})
export type CreateBriefing = z.infer<typeof createBriefingSchema>

/** Same input as creating: the estimate is for exactly that run. */
export const estimateBriefingSchema = createBriefingSchema
export type EstimateBriefing = z.infer<typeof estimateBriefingSchema>

/**
 * BILLED dollars only (D7). `pages` is null when a page count could not be
 * read from one of the files; `bytes` is always known.
 */
export const briefingEstimateSchema = z.object({
  billedMicroUsd: z.number().int().nonnegative(),
  documents: z.number().int().nonnegative(),
  pages: z.number().int().nonnegative().nullable(),
  bytes: z.number().int().nonnegative(),
  /** Null when no monthly limit is set. */
  monthRemainingMicroUsd: z.number().int().nullable(),
  /**
   * The month's limit itself, billed dollars like everything here (D7) — what
   * an admin set and the agency sees anyway. Lets the dialog warn at 80 %.
   * Null when no limit is set.
   */
  monthLimitMicroUsd: z.number().int().nullable(),
  limitReached: z.boolean(),
})
export type BriefingEstimate = z.infer<typeof briefingEstimateSchema>

/* ----------------------------------------------------------- picker --- */

/**
 * One file linked to the estate in onOffice, as the picker shows it. Every
 * file is listed, PDF or not: a document the tool cannot read is shown greyed
 * rather than silently absent.
 */
export const estateFileSchema = z.object({
  id: z.number().int(),
  title: z.string().nullable(),
  originalName: z.string().nullable(),
  /** onOffice's file type: Dokument, Foto, Titelbild, … */
  type: z.string().nullable(),
  category: z.enum(['internal', 'external']).nullable(),
  documentAttribute: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  modifiedAt: z.iso.datetime().nullable(),
  extension: z.string().nullable(),
  /** PDF only in version 1. */
  selectable: z.boolean(),
  preselected: z.boolean(),
})
/**
 * Named `…Entry` rather than `EstateFile`: the API's onOffice reader already
 * has an `EstateFile` (the raw listing, without the picker's two verdicts).
 */
export type EstateFileEntry = z.infer<typeof estateFileSchema>

export const estateFileListSchema = z.object({
  files: z.array(estateFileSchema),
  /**
   * False when onOffice could not be asked. That must read differently from an
   * empty list: "could not ask" is not "this object has no documents".
   */
  available: z.boolean(),
})
export type EstateFileList = z.infer<typeof estateFileListSchema>

/** Files in onOffice newer than the briefing — "Es gibt neuere Unterlagen". */
export const briefingFreshnessSchema = z.object({
  newerFiles: z.array(estateFileSchema),
})
export type BriefingFreshness = z.infer<typeof briefingFreshnessSchema>
